import { Tenancy_Node_Type_Enum_Ps_Enum } from '@pypestream/api-services';

import {
  HIDDEN_COLUMNS,
  NodeColumns,
  VISUAL_COLUMN_ORDER,
  nodeTypeDependentCells,
} from '../../../stores/table-store';

/**
 * The node property panel's three tabs.
 *
 * `type` is one tab whose CONTENT and LABEL depend on the node's type, not two
 * tabs. Keeping the panel name fixed while only the label changes is
 * deliberate: switching a node from Decision to Action while the tab is open
 * must not throw the user back to Identity.
 */
export type NodeFieldTab = 'identity' | 'type' | 'data';

/**
 * Who the node IS, as opposed to what it does. Fixed, and the same for every
 * node type — these are the four columns the grid freezes at its left edge.
 */
export const IDENTITY_COLUMNS: readonly NodeColumns[] = [
  NodeColumns.nodeNumber,
  NodeColumns.nodeType,
  NodeColumns.nodeName,
  NodeColumns.utterances,
];

/**
 * ## Why this is DERIVED and not a hand-written schema
 *
 * The design prototype carried its own `COLUMNS` array with a `group` field per
 * column. Transcribing that would have created a second, silently divergent
 * copy of two things the store already owns:
 *
 * - **Which columns belong to which node type** is `nodeTypeDependentCells`.
 *   That map is what the GRID greys out (`cell.tsx`) and what
 *   `updateRowType` clears on a type change, so a panel that disagreed with it
 *   would offer an editor for a field the grid shows as inapplicable and the
 *   store wipes on the next type change.
 * - **What order fields appear in** is `VISUAL_COLUMN_ORDER`, the rendered
 *   left-to-right order of the grid. Reading the panel top-to-bottom therefore
 *   matches reading the row left-to-right.
 *
 * Checked against the prototype: deriving from these two produces the
 * prototype's field list and order exactly, tab for tab. So there was nothing
 * to transcribe — only something to look up.
 *
 * Note the INVERSION that makes this work: `nodeTypeDependentCells[t]` is the
 * set CLEARED for type `t`, so the columns that BELONG to a decision node are
 * the ones cleared on an action node, and vice versa.
 */
const DECISION_COLUMNS = nodeTypeDependentCells[
  Tenancy_Node_Type_Enum_Ps_Enum.A
] as readonly NodeColumns[];

const ACTION_COLUMNS = nodeTypeDependentCells[
  Tenancy_Node_Type_Enum_Ps_Enum.D
] as readonly NodeColumns[];

/**
 * The columns the panel will never show, whatever tab they would land in.
 *
 * `HIDDEN_COLUMNS` (PSTUDIO-1345) is the single lever for removing a column
 * from the whole UI — the grid, keyboard navigation and the clipboard export
 * all consult it. `nodeTags` and `platformFlag` are behind it today. The
 * prototype drew both in Data & tracking, but honouring the drawing would make
 * this panel the one surface that ignores the lever, so a column "hidden"
 * everywhere else would be editable here. If product wants them back, the fix
 * is to delete them from `HIDDEN_COLUMNS` and they appear in this panel
 * automatically — no change needed in this file.
 *
 * The service-only columns (`id`, `order`, `intent`, `solutionVersionId`,
 * `settings`) need no exclusion: none of them is in `VISUAL_COLUMN_ORDER`.
 */
const isShown = (column: NodeColumns) => !HIDDEN_COLUMNS.has(column);

export type NodeFieldGroups = Record<NodeFieldTab, NodeColumns[]>;

/**
 * The fields each tab shows for a node of `nodeType`, in grid order.
 *
 * A group row (`g`) has no type-specific columns at all — `nodeTypeDependentCells.g`
 * is empty, so nothing is cleared and every column would read as applicable.
 * The panel does not open for group rows (see `tableStore.selectedNodeRowId`);
 * this returns an empty `type` tab for one rather than throwing, so a caller
 * that reaches here anyway degrades instead of crashing.
 */
export const getNodeFieldGroups = (
  nodeType: Tenancy_Node_Type_Enum_Ps_Enum | undefined
): NodeFieldGroups => {
  const typeColumns =
    nodeType === Tenancy_Node_Type_Enum_Ps_Enum.D
      ? DECISION_COLUMNS
      : nodeType === Tenancy_Node_Type_Enum_Ps_Enum.A
        ? ACTION_COLUMNS
        : [];

  const inTypeTab = new Set<NodeColumns>(typeColumns);
  const inIdentityTab = new Set<NodeColumns>(IDENTITY_COLUMNS);

  // One pass over the VISUAL order, so every tab is ordered the same way the
  // grid is and no column can land in two tabs.
  const groups: NodeFieldGroups = { identity: [], type: [], data: [] };

  VISUAL_COLUMN_ORDER.filter(isShown).forEach((column) => {
    if (inIdentityTab.has(column)) {
      groups.identity.push(column);
      return;
    }
    if (inTypeTab.has(column)) {
      groups.type.push(column);
      return;
    }
    // Anything left is common to both node types — but only if it is not
    // type-specific for the OTHER type, which would mean it is cleared on this
    // node and must not be offered as an editor.
    if (DECISION_COLUMNS.includes(column) || ACTION_COLUMNS.includes(column)) {
      return;
    }
    groups.data.push(column);
  });

  // Project Bobcat (Edan's review, Oct 27): an action node's Parameter Input
  // is what most action nodes need, so it leads the tab instead of sitting
  // fifth. The rest keep grid order.
  if (nodeType === Tenancy_Node_Type_Enum_Ps_Enum.A) {
    const at = groups.type.indexOf(NodeColumns.parameterInput);
    if (at > 0) {
      groups.type.splice(at, 1);
      groups.type.unshift(NodeColumns.parameterInput);
    }
  }

  return groups;
};

/** The middle tab's label — it names the node's type, and only that changes. */
export const getTypeTabLabel = (
  nodeType: Tenancy_Node_Type_Enum_Ps_Enum | undefined
): string =>
  nodeType === Tenancy_Node_Type_Enum_Ps_Enum.A
    ? 'Action node'
    : 'Decision node';

/**
 * Short orienting line under the tab bar, per the design.
 *
 * The `type` tab's line depends on the node type for the same reason its label
 * does — "what the customer sees or hears" describes a Decision node and would
 * be simply wrong above an Action node's Command and Parameter Input.
 */
export const getTabDescription = (
  tab: NodeFieldTab,
  nodeType: Tenancy_Node_Type_Enum_Ps_Enum | undefined
): string => {
  switch (tab) {
    case 'identity':
      return 'How this node is identified and matched to what a customer says.';
    case 'type':
      return nodeType === Tenancy_Node_Type_Enum_Ps_Enum.A
        ? 'What this node takes in, the command it runs, and what it hands back.'
        : 'What the customer sees or hears, and what they can answer with.';
    case 'data':
    default:
      return 'Values and events this node reads or writes.';
  }
};
